import { createCallable } from './functions';

// Define the types for the element operations
interface CombineElementsParams {
  rule: string;
  element1: string;
  element2: string;
}

interface DeconstructElementParams {
  element: string;
  forgeType: string;
}

interface ElementResult {
  name: string;
  color: string;
  formatted: string;
  description?: string; // Brief description of the element
}

interface CombineElementsResult {
  success: boolean;
  result?: ElementResult;
  error?: string;
}

interface DeconstructElementResult {
  success: boolean;
  result?: {
    components: ElementResult[];
  };
  error?: string;
}

// Create callable functions for element operations (no auth required)
const proxyForgeElementsCallable = createCallable('proxyForgeElements', false);
const proxyDeconstructElementCallable = createCallable('proxyDeconstructElement', false);

/**
 * Combine two elements using the Firebase Cloud Function
 * @param rule The type of forge (Item, Location, Character)
 * @param element1 The first element
 * @param element2 The second element
 * @returns The result of combining the elements
 */
export async function proxyForgeElements(
  rule: string,
  element1: string,
  element2: string
): Promise<ElementResult> {
  //console.log(
  //`[proxyForgeElements] Called with rule=${rule}, element1=${element1}, element2=${element2}`
  //);

  try {
    const params: CombineElementsParams = {
      rule,
      element1,
      element2,
    };

    //console.log('[proxyForgeElements] Calling Cloud Function with params:', params);
    const result = (await proxyForgeElementsCallable(params)) as CombineElementsResult;
    //console.log('[proxyForgeElements] Cloud Function result:', result);

    if (!result.success || !result.result) {
      console.error('[proxyForgeElements] Function returned error:', result.error);
      throw new Error(result.error || 'Failed to combine elements');
    }

    //console.log('[proxyForgeElements] Returning successful result:', result.result);
    return result.result;
  } catch (error: any) {
    console.error('[proxyForgeElements] Error:', error);
    // Return a default result on error
    return {
      name: 'Mystery',
      color: '#8B5CF6',
      formatted: 'Mystery - #8B5CF6',
      description: 'An unknown element created by an unexpected reaction.',
    };
  }
}

/**
 * Deconstruct an element using the Firebase Cloud Function
 * @param element The element to deconstruct
 * @param forgeType The type of forge (Item, Location, Character)
 * @returns The components of the deconstructed element
 */
export async function proxyDeconstructElement(
  element: string,
  forgeType: string
): Promise<{ components: ElementResult[] }> {
  //console.log(`[proxyDeconstructElement] Called with element=${element}, forgeType=${forgeType}`);

  try {
    const params: DeconstructElementParams = {
      element,
      forgeType,
    };

    //console.log('[proxyDeconstructElement] Calling Cloud Function with params:', params);
    const result = (await proxyDeconstructElementCallable(params)) as DeconstructElementResult;
    //console.log('[proxyDeconstructElement] Cloud Function result:', result);

    if (!result.success || !result.result) {
      console.error('[proxyDeconstructElement] Function returned error:', result.error);
      throw new Error(result.error || 'Failed to deconstruct element');
    }

    //console.log('[proxyDeconstructElement] Returning successful result:', result.result);
    return result.result;
  } catch (error: any) {
    console.error('[proxyDeconstructElement] Error:', error);
    // Return default components on error
    return {
      components: [
        {
          name: 'Material',
          color: '#3b82f6',
          formatted: 'Material - #3b82f6',
          description: 'A basic building material found in many objects.',
        },
        {
          name: 'Component',
          color: '#ef4444',
          formatted: 'Component - #ef4444',
          description: 'A fundamental part that makes up complex objects.',
        },
      ],
    };
  }
}
